import React from 'react';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { ARTICLE_TYPES } from '../../../shared/constants';
import {
  IArticle,
  IContentfulLink,
  IParentPage,
  Tag,
} from '../../../types/shared';

import PlayIcon from '../../atoms/icons/PlayIcon';
import PodcastIcon from '../../atoms/icons/PodcastIcon';
import Link from '../../atoms/link/Link';

import { DEQ_TYPES } from '../../../utils/deq';
import { getInternalLink } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { H3, SmallText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

interface Props {
  contentType?: string;
  tags?: Tag[];
  image?: IGatsbyImageData;
  title: string;
  data?: IArticle;
  primaryLink?: IContentfulLink;
  articleType: string;
  metaTime: string;
  imageHeight?: 'small' | 'large';
}

const ArticlePreviewCard = ({
  contentType,
  tags,
  image,
  title,
  data,
  primaryLink,
  articleType,
  metaTime,
  imageHeight = 'large',
}: Props) => {
  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('nl-NL').replace(/-/g, '/');
  const link =
    articleType === ARTICLE_TYPES.external
      ? (primaryLink?.externalUrl as string)
      : `/${getInternalLink(
          data?.parentPage as IParentPage,
          data?.slug as string
        )}`;

  return (
    <StyledLink
      href={link}
      data-testid={`article-preview-card ${articleType}`}
      trackingComponent="article-preview-card"
      trackingType={DEQ_TYPES.btnCard}
      trackingLabel={title}
    >
      {image && (
        <ImageContainer imageHeight={imageHeight}>
          <StyledGatsbyImage image={image} alt="" />
          {(contentType === 'podcast' || contentType === 'talkshow') && (
            <Label>
              {contentType === 'podcast' ? <PodcastIcon /> : <PlayIcon />}
            </Label>
          )}
        </ImageContainer>
      )}
      <TextContainer>
        {tags?.length ? (
          <StyledList>
            {tags.map((tag, index) => (
              <StyledListItem key={index}>
                <StyledTag>{tag.name}</StyledTag>
              </StyledListItem>
            ))}
          </StyledList>
        ) : undefined}
        {title && <Title data-testid="article-preview-title">{title}</Title>}
        {metaTime && <ContentMetaData>{formatDate(metaTime)} </ContentMetaData>}
      </TextContainer>
    </StyledLink>
  );
};

export default ArticlePreviewCard;

const StyledLink = styled(Link)`
  background: ${({ theme }) => theme.articlePreviewCard.background};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationTile};
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: all 0.2s ease-in-out;

  img {
    transition: transform 0.2s ease-in-out;
  }

  &:hover {
    box-shadow: ${({ theme }) => theme.elevations.abnElevationProduct};

    img {
      transform: scale(1.05);
    }

    h3 {
      color: ${({ theme }) => theme.articlePreviewCard.hoverColor};
    }
  }

  &:active {
    box-shadow: none;
  }
`;

const ImageContainer = styled.div<{ imageHeight?: string }>`
  height: ${({ imageHeight }) => (imageHeight === 'large' ? '233' : '173')}px;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const StyledGatsbyImage = styled(GatsbyImage)`
  height: 100%;
  object-fit: cover;
  transition: all 0.2s ease-in-out;
  width: 100%;
`;

const TextContainer = styled.div`
  color: ${({ theme }) => theme.articlePreviewCard.metaColor};
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
  padding: ${({ theme }) =>
    theme.articlePreviewCard.paddingTextContainer.default};
`;

const Label = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.abnGreen100};
  bottom: 0;
  color: ${Colors.white};
  display: flex;
  left: 0;
  padding: 8px;
  position: absolute;
  z-index: 1;

  > svg {
    display: none;
  }

  ${mq.from.breakpoint.M`
    padding: 16px;

    > svg {
      display: block;
    }
  `}

  ${mq.from.breakpoint.L`
    padding: 24px 16px;
  `}
`;

const StyledList = styled.ul`
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${Spacings.XS};
  list-style: none;
  margin: 0;
  padding: 0;
`;

const StyledListItem = styled.li`
  margin: 0;
  padding: 0;
`;

const StyledTag = styled(SmallText)`
  background-color: ${({ theme }) =>
    theme.articlePreviewCard.categoryBackground};
  border-radius: ${({ theme }) => theme.radiuses.M};
  color: ${({ theme }) => theme.articlePreviewCard.tagColor};
  padding: ${({ theme }) => theme.articlePreviewCard.tagPadding};
  width: fit-content;
`;

const Title = styled(H3)`
  color: ${({ theme }) => theme.articlePreviewCard.titleColor};
  hyphens: auto;
  word-break: break-word;
`;

const ContentMetaData = styled(SmallText)`
  color: ${({ theme }) => theme.articlePreviewCard.metaColor};
  font-weight: 500;
`;
